import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm, type FieldErrors } from 'react-hook-form';
import { toast } from 'sonner';
import { Form } from '@/components/ui/form';
import { ApiError } from '@/lib/api-client';
import {
  buildAutosavePayload,
  useServerAutosave,
  combineAutosave,
  type UseServerAutosaveReturn,
} from '@/hooks/useServerAutosave';
import { useHydrateOnce } from '@/hooks/useHydrateOnce';
import { useIsAdmin } from '@/features/auth/hooks';
import { PageHero } from '@/components/layout/PageHero';
import { useScrollToTop } from '@/components/layout/scroll-container';
import {
  useCompanies,
  useCompany,
  useCreateCompany,
  useDeleteJurisdiction,
  useSubmitCompany,
  useUpdateCompany,
  useUpsertJurisdiction,
} from '@/features/customer/hooks';
import {
  marketJurisdictionsToPersist,
  mergeCompanyValues,
  mergeFacilitiesField,
  splitCompanyValues,
} from '@/features/customer/company-data';
import {
  FLAT_FORM_MARKETS,
  isFlatFormMarket,
  marketShortName,
} from '@/features/customer/markets';
import { createWriteQueue, runCoordinatedSave, whenSaved } from '@/features/customer/save-coordinator';
import { AmendmentBanner } from '@/features/customer/components/AmendmentBanner';
import { customerPostSubmitRedirect } from '@/features/customer/post-submit';
import { stampSignatureDate } from '@/features/customer/signature-date';
import { DraftStatusPill } from '@/features/customer/components/DraftStatusPill';
import {
  CompanyIdentificationSection,
  SECTION_1_FIELDS,
} from '@/features/customer/components/CompanyIdentificationSection';
import {
  CorporateHeadquartersSection,
  SECTION_2_FIELDS,
} from '@/features/customer/components/CorporateHeadquartersSection';
import {
  CorporateProfileSection,
  SECTION_14_FIELDS,
} from '@/features/customer/components/CorporateProfileSection';
import {
  LegalAuthorizationSection,
  SECTION_13_FIELDS,
} from '@/features/customer/components/LegalAuthorizationSection';
import {
  ProductResponsibilitySection,
  SECTION_PRODUCT_RESP_FIELDS,
} from '@/features/customer/components/ProductResponsibilitySection';
import {
  FinancialDisclosureSection,
  SECTION_FINANCIAL_FIELDS,
} from '@/features/customer/components/FinancialDisclosureSection';
import {
  FdaContactSection,
  SECTION_3_FIELDS,
} from '@/features/customer/components/FdaContactSection';
import {
  EstablishmentRegistrationSection,
  SECTION_5_FIELDS,
} from '@/features/customer/components/EstablishmentRegistrationSection';
import {
  QualitySystemSection,
  SECTION_7_FIELDS,
} from '@/features/customer/components/QualitySystemSection';
import { SupportingDocumentsSection } from '@/features/customer/components/SupportingDocumentsSection';
import { TargetMarketsHub } from '@/features/customer/components/jurisdictions/TargetMarketsHub';
import { Button } from '@/components/ui/button';
import { WizardFooter } from '@/features/customer/components/WizardFooter';
import type { MarketValidator } from '@/features/customer/components/jurisdictions/useReportMarketValidity';
import { WizardSectionNav } from '@/components/WizardSectionNav';
import { WizardSectionPanel } from '@/components/WizardSectionPanel';
import { FormSkeleton } from '@/components/FormSkeleton';
import {
  customerAccountDefaults,
  customerAccountResolver,
  MARKET_RULE_TRIGGER_FIELDS,
  needsEuAuthorisedRep,
  type CustomerAccountContext,
  type CustomerAccountValues,
} from '@/schemas/customer-account';

/**
 * Customer Account — two-stage flow on one shared RHF form.
 *
 *   Stage 1 (profile): the guided base-profile wizard — who the company IS,
 *     filled once. Its final CTA advances to stage 2 instead of submitting.
 *   Stage 2 (markets): the Target Markets hub — add a regulatory module per
 *     market the company sells INTO, each building on the saved base profile.
 *     Stage 2 owns the real account submit.
 *
 * Owns:
 *   - One RHF instance covering the base sections + the (US) market fields
 *   - Wizard step state (1..N) for stage 1 + the `stage` toggle
 *   - Per-section validation on Next; full-schema validation on final submit
 *
 * Each section component is a pure renderer that receives `form` via prop and
 * writes into the shared draft.
 */
export const Route = createFileRoute('/customer-account_/new')({
  component: CustomerAccountPage,
  // ?id=N → operate on that company; absent → create a new one. The id
  // ALONE doesn't mean "editing" — creation also gains ?id after the first
  // autosave persists the row. ``mode=edit`` marks entries from an Edit
  // action (the breadcrumb keys on it; the wizard itself derives its
  // amend/restore behavior from the company's status, not the URL).
  validateSearch: (
    search: Record<string, unknown>,
  ): { id?: number; mode?: 'edit' } => {
    const raw = search.id;
    const id = raw == null || raw === '' ? undefined : Number(raw);
    const out: { id?: number; mode?: 'edit' } = {};
    if (id != null && Number.isFinite(id)) out.id = id;
    if (search.mode === 'edit') out.mode = 'edit';
    return out;
  },
});

interface RenderCtx {
  /** Other companies in the account, for the Parent Company picker (section 1). */
  parentOptions: { id: number; name: string }[];
  /** The persisting company — for sections that talk to the server directly
   *  (Section 12 uploads documents to /api/companies/{id}/documents). */
  companyId?: number;
}

interface SectionDef {
  num: number;
  name: string;
  fields: readonly (keyof CustomerAccountValues)[];
  render: (
    form: ReturnType<typeof useForm<CustomerAccountValues>>,
    ctx: RenderCtx,
  ) => React.ReactNode;
}

const SECTIONS: SectionDef[] = [
  {
    num: 1,
    name: 'Legal Company Identification',
    fields: SECTION_1_FIELDS,
    render: (form, ctx) => (
      <CompanyIdentificationSection form={form} parentOptions={ctx.parentOptions} />
    ),
  },
  {
    num: 2,
    name: 'Registered Address & Contact',
    fields: SECTION_2_FIELDS,
    render: (form) => <CorporateHeadquartersSection form={form} />,
  },
  {
    num: 3,
    name: 'Regulatory Contacts',
    fields: SECTION_3_FIELDS,
    render: (form) => <FdaContactSection form={form} />,
  },
  {
    num: 4,
    name: 'Facilities & Sites Information',
    fields: SECTION_5_FIELDS,
    render: (form) => <EstablishmentRegistrationSection form={form} />,
  },
  {
    num: 5,
    name: 'Quality System Information',
    fields: SECTION_7_FIELDS,
    render: (form) => <QualitySystemSection form={form} />,
  },
  {
    num: 6,
    name: 'Product Responsibility Declaration',
    fields: SECTION_PRODUCT_RESP_FIELDS,
    render: (form) => <ProductResponsibilitySection form={form} />,
  },
  {
    num: 7,
    name: 'Financial Disclosure',
    fields: SECTION_FINANCIAL_FIELDS,
    render: (form) => <FinancialDisclosureSection form={form} />,
  },
  {
    num: 8,
    name: 'Legal and Authorization Statements',
    fields: SECTION_13_FIELDS,
    render: (form) => <LegalAuthorizationSection form={form} />,
  },
  {
    num: 9,
    name: 'Corporate Profile Narrative',
    fields: SECTION_14_FIELDS,
    render: (form) => <CorporateProfileSection form={form} />,
  },
  {
    num: 10,
    name: 'Supporting Documents',
    // No form fields — documents upload directly to the server (multipart),
    // so there's nothing to validate on Next.
    fields: [],
    render: (_form, ctx) => <SupportingDocumentsSection companyId={ctx.companyId} />,
  },
];

const TOTAL_STEPS = SECTIONS.length;

/**
 * Turn a zod `.message` into a compact field name for a validation toast.
 * Nearly every required-field message in this schema already reads as
 * "<Field name> is required", "Select <field>", or "Enter <field>" — strip
 * the boilerplate rather than showing the whole sentence. A message that
 * doesn't match one of those templates (a format error, e.g. the email
 * domain regex) is shown as-is; it still names what's wrong.
 */
export function fieldNameFromMessage(message: string): string {
  const required = message.match(/^(.*?)\s+is required\b/i);
  if (required) return capitalizeFirst(required[1]!);
  const select = message.match(/^Select (?:the |an? )?(.+)$/i);
  if (select) return capitalizeFirst(select[1]!);
  const enter = message.match(/^Enter (?:the |an? |your )?(.+)$/i);
  if (enter) return capitalizeFirst(enter[1]!);
  return capitalizeFirst(message);
}

/** Every entry in the "Required Fields Missing" list reads sentence-case
 *  regardless of which template it was stripped from ("Select facility
 *  type" leaves "facility type" lowercase; "City is required" already
 *  leaves "City" capitalized) — capitalize just the first character so the
 *  list reads consistently without title-casing multi-word names. */
function capitalizeFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Every `.message` under one react-hook-form error node, walking into
 * nested field arrays (Section 4's `facilities` rows) so a row-level error
 * isn't silently dropped from a section's summary — `errs.facilities` on its
 * own carries no `.message` unless the array-level refine fired; a row's
 * `legal_form_other` lives one level deeper. Skips RHF's own bookkeeping
 * keys (`type`/`ref`/`types`) so this never walks into a DOM ref.
 */
export function collectFieldMessages(node: unknown): string[] {
  if (!node || typeof node !== 'object') return [];
  const obj = node as Record<string, unknown>;
  const out: string[] = typeof obj.message === 'string' && obj.message ? [obj.message] : [];
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'message' || key === 'type' || key === 'ref' || key === 'types') continue;
    out.push(...collectFieldMessages(value));
  }
  return out;
}

/** "Required Fields Missing: A, B, C" naming every offending field under
 *  `fields` — never a capped few. `undefined` when none of them error. */
export function requiredFieldsMissingText(
  errs: Record<string, unknown>,
  fields: readonly string[],
): string | undefined {
  const names = Array.from(
    new Set(fields.flatMap((f) => collectFieldMessages(errs[f])).map(fieldNameFromMessage)),
  );
  return names.length ? `Required Fields Missing: ${names.join(', ')}` : undefined;
}

function CustomerAccountPage() {
  const navigate = useNavigate();
  const { id: searchId } = Route.useSearch();
  // Shell-aware scroll-to-top (the pinned shell means the window never
  // scrolls; section changes must scroll the content container instead).
  const scrollToTop = useScrollToTop();

  const [step, setStep] = useState(1);
  // Highest step number the user may jump to via the sidebar/index — a 1-based
  // ceiling, NOT the current step. Ratchets forward only (never decreases)
  // when `firstBlockedStep()` confirms the sections passed are valid on the
  // way out. Without this, `handlePickSection` only ever validated the section
  // being LEFT, not any section skipped over: section 1 valid, section 2
  // invalid → go 1 -> 2 (blocked from going further), Back to 1, then jump
  // straight to section 5 — leaving section 1 (still valid) passes the gate,
  // so the jump landed on 5 with section 2's errors never surfaced. Fixed by
  // capping every forward jump's destination at this frontier, not just at
  // whether the CURRENT section validates.
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(1);
  // 'profile' = base wizard (stage 1); 'markets' = Target Markets hub (stage 2).
  const [stage, setStage] = useState<'profile' | 'markets'>('profile');
  // True while `firstBlockedStep()` is awaiting `form.trigger(...)`.
  // Freezes the whole nav (sidebar + Back/Next) so a second click can't land
  // mid-gate — otherwise its stale continuation (captured `step`/`stage`)
  // fires unconditionally once the pending gate resolves, after the user has
  // already navigated elsewhere. See firstBlockedStep below.
  const [gateInFlight, setGateInFlight] = useState(false);

  // Which company this wizard edits. Seeded from ?id (edit) or undefined
  // (create — set once the first autosave creates the row). Refs back the
  // create-then-update autosave so the debounced save never sees a stale id.
  const initialEditIdRef = useRef(searchId);
  const [companyId, setCompanyId] = useState<number | undefined>(searchId);
  const companyIdRef = useRef<number | undefined>(searchId);
  const creatingRef = useRef(false);
  // Mirrors creatingRef for the update phase: without it, nothing serialized
  // concurrent updates once the row existed — two overlapping full-document
  // PUTs could race, and if the older response landed last it silently
  // reverted every field changed in between. See runCoordinatedSave.
  const updatingRef = useRef(false);
  // A save requested while the create/update is still in flight. The
  // coordinator flushes it afterward so an edit made mid-flight (e.g. the
  // Parent Company pick) isn't silently dropped. See runCoordinatedSave.
  const pendingSaveRef = useRef(false);
  // The save chain running now — Submit waits on it (whenSaved) so it never submits
  // ahead of the last edit.
  const savesSettledRef = useRef<Promise<void> | null>(null);
  // The payload we last SENT successfully — the baseline a deferred flush holds
  // invalid fields back against, mirroring the hook's own last-saved baseline.
  const lastSentRef = useRef<CustomerAccountValues | null>(null);

  // Markets the user has added (seeded from persisted jurisdictions in edit
  // mode). Ref-backed so the debounced autosave reads the current set.
  const [addedMarkets, setAddedMarkets] = useState<string[]>([]);
  const addedMarketsRef = useRef<string[]>([]);
  // EU / UK keep their own forms; each hands us a validator that Submit runs at
  // click time, so the check is never stale and problems are marked red.
  const marketValidatorsRef = useRef<Record<string, MarketValidator>>({});
  const handleRegisterMarketValidator = useCallback(
    (code: string, validate: MarketValidator | null) => {
      if (validate) marketValidatorsRef.current[code] = validate;
      else delete marketValidatorsRef.current[code];
    },
    [],
  );
  // Submit found a problem inside a market — show that market's tab.
  const [marketFocus, setMarketFocus] = useState<{ code: string; seq: number } | null>(null);
  const focusMarketTab = (code: string) => setMarketFocus({ code, seq: Date.now() });

  const companiesQuery = useCompanies();
  const companyQuery = useCompany(companyId);
  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();
  const submitMutation = useSubmitCompany();
  const upsertJurisdictionMutation = useUpsertJurisdiction();
  const deleteJurisdictionMutation = useDeleteJurisdiction();

  // Only the added markets' fields and rules count (markets are independent). A
  // getter over the ref, not a per-render snapshot, so a check that runs mid-click
  // (e.g. while "Add market" is still saving) already sees the new list.
  const [validationContext] = useState<CustomerAccountContext>(() => ({
    get addedMarkets() {
      return addedMarketsRef.current;
    },
  }));

  const form = useForm<CustomerAccountValues, CustomerAccountContext>({
    resolver: customerAccountResolver,
    context: validationContext,
    defaultValues: customerAccountDefaults,
    mode: 'onBlur',
  });

  // Create mode is ready immediately; edit mode waits for the company to load.
  // Gate on the INITIAL edit id (captured in a ref) so the post-create URL
  // change to ?id=N doesn't re-gate the UI and flash the loader mid-typing.
  const startedInEdit = initialEditIdRef.current != null;
  const draftLoaded = !startedInEdit || !companyQuery.isLoading;
  // Fresh, not just loaded. Navigating here from the Companies list is a client-side
  // route change, so React Query's in-memory cache for ['companies', companyId]
  // survives — `isLoading` is false the instant there's ANY cached entry, stale or
  // not, while a background refetch (staleTime 15s) silently fetches the real one.
  // A true browser refresh has no cache to hit and always reads fresh, which is why
  // this only ever showed up navigating in from the list. `useHydrateOnce` only ever
  // hydrates once, so hydrating off a stale cache hit permanently locks the form to
  // whatever was cached earlier in the session (confirmed: an already-stale
  // `parent_company_id` from before it was ever correctly set) — the real value
  // that lands moments later from the background refetch is never applied, and
  // autosave then "corrects" the server back to the stale one. Waiting out
  // `isFetching` too means hydration only ever fires once the freshest available
  // data has actually landed. `isSuccess`, not just `!isLoading` — both flip
  // false on a query ERROR too, which would otherwise let hydrate's callback
  // run with `companyQuery.data` still undefined: `form.reset()` is skipped
  // (guarded by `if (company)`), but `hasHydrated` latches true anyway since
  // useHydrateOnce fires unconditionally once — permanently locking the form
  // to its blank defaults (parent_company === '') with autosave now enabled
  // to "correct" the server back to that on the next edit.
  const companyDataFresh = !startedInEdit || (companyQuery.isSuccess && !companyQuery.isFetching);
  const mode: 'create' | 'restore' | 'amend' = (() => {
    if (!startedInEdit) return 'create';
    return companyQuery.data?.status === 'submitted' ? 'amend' : 'restore';
  })();

  // Hydrate ONCE when the company is loaded AND fresh (edit mode). Create mode has
  // no company, so this is a no-op there. Hydrating once (not on every cache
  // write) keeps autosave from resetting the form mid-typing. See useHydrateOnce.
  // `hasHydrated` (not `draftLoaded`) gates autosave below — see useHydrateOnce's
  // doc comment for why using the raw flag latches a pre-reset baseline.
  const hasHydrated = useHydrateOnce(companyDataFresh, () => {
    const company = companyQuery.data;
    if (company) {
      form.reset({
        ...mergeCompanyValues(company.base, company.jurisdictions),
        parent_company:
          company.parent_company_id != null ? String(company.parent_company_id) : '',
      } as CustomerAccountValues);
    }
    // Latch the same baseline useServerAutosave latches on its first run (it's
    // gated on `hasHydrated`, so both see the post-reset values). Without this
    // `lastSentRef` stays null until the first successful save, and a hold-back
    // built against a null baseline treats EVERY key as changed — validating the
    // whole form and dropping any key that isn't valid yet. That would turn an
    // "Add market" click on an untouched form into a full-form validation pass.
    lastSentRef.current = form.getValues();
  });

  // Reopening an existing company (`startedInEdit`, i.e. mounted with `?id=`):
  // re-derive the frontier from the hydrated data itself, the same way a live
  // forward-jump does — validate each section in order and stop at the first
  // one that doesn't pass (see firstBlockedStep below). Amend and restore
  // mode are treated identically: a submitted company isn't guaranteed valid
  // (autosave persists a cleared required field), and a fully valid one
  // still scans through to TOTAL_STEPS, unlocking every section.
  useEffect(() => {
    if (!hasHydrated || !startedInEdit) return;
    let cancelled = false;
    (async () => {
      let ceiling = TOTAL_STEPS;
      for (const section of SECTIONS) {
        if (section.fields.length === 0) continue;
        const valid = await form.trigger([...section.fields]);
        if (!valid) {
          ceiling = section.num;
          break;
        }
      }
      if (cancelled) return;
      // `form.trigger()` above is a real validation pass — it populates
      // `formState.errors` same as a genuine Next attempt would, which paints
      // red messages on whichever section is on screen (always section 1,
      // since `step` starts there). Nobody asked to leave it; this was a
      // background probe, not a user action. Clear what it painted, but only
      // on fields the user hasn't actually blurred themselves in the
      // meantime — a real error from a real interaction during this async
      // window should survive.
      const touched = form.formState.touchedFields as Partial<
        Record<(typeof SECTIONS)[0]['fields'][number], unknown>
      >;
      const untouched = SECTIONS[0]!.fields.filter((f) => !touched[f]);
      if (untouched.length > 0) form.clearErrors(untouched);
      // Ratchet, don't overwrite: `handleNext`/`handlePickSection` can widen
      // `maxUnlockedStep` while this async scan is still in flight (it's not
      // gated by `gateInFlight`), so a plain `setMaxUnlockedStep(ceiling)`
      // could stomp a higher value the user already legitimately reached
      // back down to this scan's (now-stale) result.
      setMaxUnlockedStep((m) => Math.max(m, ceiling));
    })();
    return () => {
      cancelled = true;
    };
  }, [hasHydrated, startedInEdit, form]);

  // Surface persisted markets as "added" whenever the company loads or
  // refetches, so an existing company shows its active markets immediately
  // (as tabs) instead of an empty hub + dropdown. Unions with in-session adds;
  // a removed market is deleted server-side, so the refetch drops it.
  useEffect(() => {
    // Skip markets the user removed this session: a refresh that left the server before
    // the removal landed still lists them, and unioning it back re-added the market (the
    // next autosave then re-created it). Re-adding a market clears it from this set.
    const persisted = (companyQuery.data?.jurisdictions.map((j) => j.market_code) ?? []).filter(
      (m) => !removedMarketsRef.current.has(m),
    );
    setAddedMarkets((prev) => {
      const allPresent = persisted.every((m) => prev.includes(m));
      if (allPresent) return prev;
      const merged = Array.from(new Set([...prev, ...persisted]));
      addedMarketsRef.current = merged;
      return merged;
    });
  }, [companyQuery.data]);

  const isAdmin = useIsAdmin();

  // Parent Company picker options: every OTHER company in the account. Memoized —
  // this page re-renders on every keystroke (form.watch() feeds autosave above),
  // so an inline array/object literal here got a brand-new reference every render,
  // tearing down and rebuilding every <SelectItem> each time. Keeping the array
  // stable across renders that don't change the company list avoids that churn.
  // (The `onValueChange('')` that wiped a saved parent was traced to a different
  // cause — Radix's hidden native <select> resetting when the controlled value has
  // no matching <option> yet; see the guard in CompanyIdentificationSection.)
  const parentOptions = useMemo(
    () =>
      (companiesQuery.data ?? [])
        .filter((c) => c.id !== companyId)
        .map((c) => ({
          id: c.id,
          name:
            (typeof c.base.legal_company_name === 'string' &&
              c.base.legal_company_name.trim()) ||
            'Untitled company',
        })),
    [companiesQuery.data, companyId],
  );

  // Split the flat form into company.base + jurisdiction['US'], create the row
  // on first save, then update. Delegated to runCoordinatedSave, which guards
  // against double-creates AND flushes any edit made while the create is still
  // in flight — otherwise the autosave treats the skipped save as successful
  // and silently drops it (this is what lost the Parent Company on a brand-new
  // company, reverting a child to a top-level filer on refresh).
  // A blank value always saves: the hold-back exists for half-typed values
  // (a 6-digit FEI, a malformed email), not deletions. Holding a blanked
  // required field back re-sent its old value, so a clear — by hand or by
  // unchecking "Same as Headquarters Address" — came back on reload.
  // Required-ness is enforced by the section exit gate and submit instead.
  const validateFields = (names: string[]) => {
    const values = form.getValues() as Record<string, unknown>;
    const filled = names.filter((n) => values[n] != null && values[n] !== '');
    return filled.length === 0 || form.trigger(filled as (keyof CustomerAccountValues)[]);
  };

  // `facilities` is a useFieldArray list, but changedTopLevelKeys/validateFields
  // treat it as ONE key — an invalid row anywhere (e.g. "Other" legal form left
  // blank) would otherwise revert every row, including unrelated edits made to
  // other rows in the same tick. Hold back only the broken row(s); everything
  // else in the array still saves.
  const mergeInvalidField = (key: string, previous: unknown, next: unknown) => {
    if (key !== 'facilities') return previous;
    const rowErrors = form.formState.errors.facilities;
    return mergeFacilitiesField(previous, next, (i) => !!rowErrors?.[i]);
  };

  // The live form values with invalid fields held back — what any save OUTSIDE
  // the autosave hook must send. Reading form.getValues() directly would walk
  // past the hold-back and persist a half-typed value the server then rejects.
  const heldBackFormValues = () =>
    buildAutosavePayload({
      next: form.getValues(),
      previous: lastSentRef.current,
      validateFields,
      mergeInvalidField,
    });

  // Every market save and removal runs through ONE queue, in the order the user acted:
  // a "Remove market" clicked mid-save waits for that save instead of racing it (the save
  // landing second re-created the removed market with its old data).
  const [enqueueMarketWrite] = useState(createWriteQueue);
  // Markets removed this session — a late refresh mustn't bring them back (see the
  // persisted-markets effect).
  const removedMarketsRef = useRef<Set<string>>(new Set());
  // The EU / UK tabs save themselves; this puts their saves in the same queue, each
  // skipped if its market was removed while it waited.
  const queueMarketWrite = useCallback(
    (code: string, write: () => Promise<unknown>) =>
      enqueueMarketWrite(async () => {
        if (addedMarketsRef.current.includes(code)) await write();
      }),
    [enqueueMarketWrite],
  );

  async function saveCompany(values: CustomerAccountValues) {
    const splitForSave = (v: CustomerAccountValues) => {
      const { base, markets } = splitCompanyValues(v);
      const parentRaw = (base as Record<string, unknown>).parent_company;
      delete (base as Record<string, unknown>).parent_company; // FK, not base data
      const parentCompanyId =
        typeof parentRaw === 'string' && parentRaw !== '' ? Number(parentRaw) : null;
      return { base, markets, parentCompanyId };
    };
    // Persist each flat-form market's carve-out, but only if the user ADDED it
    // (honest: a market exists iff added — without the gate every company would
    // show a phantom market for the empty fields it carries). Registry-driven:
    // a new flat-form country persists automatically.
    const persistMarkets = async (
      cid: number,
      markets: Record<string, Record<string, unknown>>,
    ) => {
      for (const { marketCode, data } of marketJurisdictionsToPersist(
        markets,
        addedMarketsRef.current,
      )) {
        await enqueueMarketWrite(async () => {
          // Removed while this save was waiting its turn — don't write it back.
          if (!addedMarketsRef.current.includes(marketCode)) return;
          await upsertJurisdictionMutation.mutateAsync({ companyId: cid, marketCode, data });
        });
      }
    };
    await runCoordinatedSave(
      {
        creating: creatingRef,
        updating: updatingRef,
        pending: pendingSaveRef,
        settled: savesSettledRef,
      },
      {
        hasRow: () => companyIdRef.current != null,
        latest: heldBackFormValues,
        create: async (v) => {
          const { base, markets, parentCompanyId } = splitForSave(v);
          const created = await createMutation.mutateAsync({ base, parentCompanyId });
          companyIdRef.current = created.id;
          setCompanyId(created.id);
          void navigate({
            to: '/customer-account/new',
            search: { id: created.id },
            replace: true,
          });
          await persistMarkets(created.id, markets);
          // An EU / UK market added while the company didn't exist yet (or while this
          // create was in flight) has no row: write it now, so it survives a reload
          // without the user having to type in its tab first.
          for (const code of addedMarketsRef.current) {
            if (isFlatFormMarket(code)) continue;
            await enqueueMarketWrite(async () => {
              if (!addedMarketsRef.current.includes(code)) return;
              await upsertJurisdictionMutation.mutateAsync({ companyId: created.id, marketCode: code, data: {} });
            });
          }
          lastSentRef.current = v;
        },
        update: async (v) => {
          const { base, markets, parentCompanyId } = splitForSave(v);
          const cid = companyIdRef.current!;
          await updateMutation.mutateAsync({ id: cid, base, parentCompanyId });
          await persistMarkets(cid, markets);
          lastSentRef.current = v;
        },
      },
      values,
    );
  }

  // Add a market: record it, then persist immediately so the jurisdiction row exists — a market
  // exists iff added (matches US/CA/AU). Flat-form markets live in the main form, so saveCompany
  // writes them. EU/UK live in their own self-persisting modules (which only save their DATA on
  // edit), so their "added" fact must be written here as an empty jurisdiction — otherwise an
  // added-but-untouched EU/UK is silently dropped when addedMarkets is re-derived from the backend
  // on reload. The module's autosave then fills in the fields.
  async function handleAddMarket(code: string) {
    removedMarketsRef.current.delete(code);
    const before = addedMarketsRef.current;
    const isNew = !before.includes(code);
    if (isNew) {
      const next = [...before, code];
      addedMarketsRef.current = next;
      setAddedMarkets(next);
    }
    try {
      if (isFlatFormMarket(code)) {
        await saveCompany(await heldBackFormValues());
      } else if (isNew) {
        const cid = companyIdRef.current;
        if (cid == null) {
          // No company yet: creating it writes every added EU / UK market (saveCompany's
          // create). If a create is already in flight, that one writes it when it lands.
          await saveCompany(await heldBackFormValues());
        } else {
          await enqueueMarketWrite(async () => {
            if (!addedMarketsRef.current.includes(code)) return; // removed meanwhile
            await upsertJurisdictionMutation.mutateAsync({ companyId: cid, marketCode: code, data: {} });
          });
        }
      }
    } catch (err) {
      // The persist can 400 — e.g. the admin disabled this market after our
      // 5-minute-stale catalog rendered it (MarketNotEnabledError). Without
      // this revert the tab appears while no jurisdiction row exists, and the
      // global mutation handler stays silent on 4xx by design.
      if (isNew) {
        addedMarketsRef.current = before;
        setAddedMarkets(before);
      }
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : 'Could not add this market — please retry.',
      );
    }
  }

  // Remove a market: forget it, and delete its persisted jurisdiction if any.
  async function handleRemoveMarket(code: string) {
    removedMarketsRef.current.add(code);
    const next = addedMarketsRef.current.filter((c) => c !== code);
    addedMarketsRef.current = next;
    setAddedMarkets(next);
    // Forget what was typed into a removed flat-form market, as the confirm dialog
    // promises ("its data won't come back"): re-adding it later in this session
    // starts blank instead of resurrecting — and re-saving — the old values.
    const removed = FLAT_FORM_MARKETS.find((m) => m.code === code);
    const keys = removed ? ([...removed.keys] as (keyof CustomerAccountValues)[]) : [];
    const typed = keys.map((key) => [key, form.getValues(key)] as const);
    for (const key of keys) form.setValue(key, customerAccountDefaults[key]);
    form.clearErrors(keys);
    const cid = companyIdRef.current;
    if (cid == null) return;
    try {
      await enqueueMarketWrite(() =>
        deleteJurisdictionMutation.mutateAsync({ companyId: cid, marketCode: code }),
      );
    } catch (err) {
      // 404: the market was never saved (e.g. a visualization-only module) — nothing to undo.
      if (err instanceof ApiError && err.status === 404) return;
      // Still saved on the server: put it back with what was typed, instead of hiding a
      // market that Submit would then send with its old data.
      removedMarketsRef.current.delete(code);
      if (!addedMarketsRef.current.includes(code)) {
        const back = [...addedMarketsRef.current, code];
        addedMarketsRef.current = back;
        setAddedMarkets(back);
      }
      for (const [key, value] of typed) form.setValue(key, value);
      toast.error(`Could not remove the ${marketShortName(code)} market — please retry.`);
    }
  }

  // Debounced server-only autosave. Engages once loaded AND the caller is admin
  // (non-admins are read-only; their writes 403 and would flash the pill).
  const autosave = useServerAutosave({
    values: form.watch(),
    saveFn: saveCompany,
    enabled: hasHydrated && isAdmin,
    debounceMs: 800,
    // Only re-validate the fields that actually changed this tick (mirrors
    // handleNext's per-section trigger below) — a not-yet-filled required
    // field in a section the user hasn't reached yet must never block
    // autosaving the section they're actually editing.
    validateFields,
    mergeInvalidField,
  });

  // Self-persisting markets (the EU module) report their autosave state here, so
  // the markets-stage header pill reflects EU saves alongside base/US.
  const [marketSave, setMarketSave] = useState<UseServerAutosaveReturn>({
    state: 'idle',
    lastSavedAt: null,
    lastError: null,
  });
  const marketsSave = combineAutosave(autosave, marketSave);

  // Keep formState.isValid fresh whenever the markets hub is showing — it gates
  // the Submit/Done button below. Covers reaching this stage either via
  // handleGoToMarkets (which only re-validates the LAST base section) or the
  // section-nav's direct jump to "Target Markets" (no validation at all).
  // Re-run on add/remove too: the validator only checks added markets, so the
  // button must update the moment the set changes. And when a choice switches a
  // market rule on (a foreign-manufacturer status), so the fields it now requires
  // turn red instead of the button greying with nothing marked.
  const ruleTriggers = form.watch(MARKET_RULE_TRIGGER_FIELDS).join('|');
  useEffect(() => {
    if (stage === 'markets') void form.trigger();
  }, [stage, form, addedMarkets, ruleTriggers]);

  // MDR/IVDR Art. 11: a device maker not established in the EU/EEA needs an EU
  // Authorised Representative — the EU tab enforces it when this is true.
  const euRequiresAuthorisedRep = needsEuAuthorisedRep({
    hq_country: form.watch('hq_country'),
    facilities: form.watch('facilities'),
  });

  const currentSection = SECTIONS[step - 1]!;

  // Sections holding live validation errors — drives the red badge in the
  // index so a blocked jump has a visible cause, not just a toast.
  const sectionHasErrors = (section: SectionDef) => {
    const errs = form.formState.errors as Record<string, unknown>;
    return section.fields.some((f) => errs[f] != null);
  };

  // How far the sidebar may jump right now. `maxUnlockedStep` alone would
  // block even the CURRENT section's own "attempt the next one" move on a
  // fresh wizard (step === maxUnlockedStep, nothing beyond it confirmed yet)
  // — the invariant `step <= maxUnlockedStep` (forward moves always raise the
  // frontier to at least their destination) means this only ever grants ONE
  // extra step past the frontier, and only while standing at the frontier
  // itself; after going Back, `step + 1` no longer exceeds it.
  const reachCeiling = Math.max(maxUnlockedStep, step + 1);

  // Target Markets opens once the frontier reaches the last section — only
  // possible after sections 1–9 each passed validation on the way out
  // (section 10 has no fields). Uses maxUnlockedStep, NOT reachCeiling:
  // reachCeiling grants one step past the frontier, which would unlock the
  // entry while still standing on section 9.
  const marketsUnlocked = maxUnlockedStep >= TOTAL_STEPS;

  /**
   * Gate for every FORWARD move. Validates each section from `fromStep`
   * through `throughStep` in order — the section being left AND every section
   * a far jump would skip — and returns the first one that fails (after
   * saying what's missing), or null when all pass.
   *
   * Checking only the section being left isn't enough: `maxUnlockedStep`
   * ratchets and never comes back down, so a section validated earlier can go
   * invalid again (go Back, blank a field, go Back once more, then jump ahead
   * from the sidebar). Every forward exit goes through here — Next, the
   * section index, and the Target Markets entry.
   *
   * Backward navigation is deliberately NOT VALIDATION-gated: you must always
   * be able to return to a broken section to fix it. It IS frozen for the
   * duration of this call (via `gateInFlight`, below) — not because Back
   * itself needs validating, but because leaving it clickable would let a
   * click land while THIS gate is still resolving, and that gate's stale
   * continuation would still fire on whatever it originally targeted once it
   * settles, regardless of where the user has since navigated.
   */
  async function firstBlockedStep(fromStep: number, throughStep: number): Promise<number | null> {
    setGateInFlight(true);
    try {
      for (const section of SECTIONS.slice(fromStep - 1, throughStep)) {
        if (section.fields.length === 0) continue;
        if (await form.trigger([...section.fields])) continue;

        // Name every offending field rather than a bare "fix errors" — the
        // same treatment the submit handler gives. Attention is on the nav
        // rail when a jump is blocked, so the inline messages alone are easy
        // to miss.
        const errs = form.formState.errors as Record<string, unknown>;
        toast.error(
          requiredFieldsMissingText(errs, section.fields) ??
            `Finish “${section.name}” before moving on.`,
        );
        return section.num;
      }
      return null;
    } finally {
      setGateInFlight(false);
    }
  }

  /** Land on a section the gate blocked on, so its errors are on screen. */
  function showBlockedStep(blocked: number) {
    setStage('profile');
    setStep(blocked);
    scrollToTop();
  }

  async function handleNext() {
    if ((await firstBlockedStep(step, step)) != null) return;
    setMaxUnlockedStep((m) => Math.max(m, step + 1));
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    scrollToTop();
  }

  function handleBack() {
    setStep((s) => Math.max(s - 1, 1));
    scrollToTop();
  }

  // Stage 1 → 2: validate every section still ahead, then reveal the markets
  // hub. Reachable from ANY section (not just the last), so it's subject to
  // the same frontier cap as handlePickSection: skipping straight to markets
  // past sections that were never validated would reproduce the same
  // skip-ahead bug. If the frontier isn't at the end yet, land on it instead.
  async function handleGoToMarkets() {
    const landsOnMarkets = reachCeiling >= TOTAL_STEPS;
    const blocked = await firstBlockedStep(step, landsOnMarkets ? TOTAL_STEPS : reachCeiling - 1);
    if (blocked != null) {
      if (blocked !== step) showBlockedStep(blocked);
      return;
    }
    if (reachCeiling !== maxUnlockedStep) setMaxUnlockedStep(reachCeiling);
    if (!landsOnMarkets) {
      setStage('profile');
      setStep(reachCeiling);
      scrollToTop();
      return;
    }
    setStage('markets');
    scrollToTop();
  }

  /** Jump to a base section from the index. Forward moves are gated; moving
   *  back (including back out of the markets stage) is always allowed.
   *
   *  The destination is capped at `reachCeiling`, so clicking ahead of it
   *  (impossible via the UI since those rows render `unreachable`, but
   *  defended here too) lands on the ceiling instead of the click target.
   *  Every section between here and the destination is validated on the way;
   *  the first one that fails is where the jump lands instead. */
  async function handlePickSection(index: number) {
    const goingForward = stage === 'profile' && index > step - 1;
    const targetStep = goingForward ? Math.min(index + 1, reachCeiling) : index + 1;
    if (goingForward) {
      const blocked = await firstBlockedStep(step, targetStep - 1);
      if (blocked != null) {
        if (blocked !== step) showBlockedStep(blocked);
        return;
      }
      if (reachCeiling !== maxUnlockedStep) setMaxUnlockedStep(reachCeiling);
    }
    setStage('profile');
    setStep(targetStep);
    scrollToTop();
  }

  // Submit and Done share this: a failed full-form validation jumps to the
  // first incomplete section so the inline errors are visible, and names
  // every offending field. Never silent.
  function showFirstInvalidSection(errors: FieldErrors<CustomerAccountValues>) {
    // The offending field is usually in a base section that's off-screen
    // (we're on the markets stage).
    const invalid = Object.keys(errors) as (keyof CustomerAccountValues)[];
    const idx = SECTIONS.findIndex((s) => s.fields.some((f) => invalid.includes(f)));
    const section = idx >= 0 ? SECTIONS[idx] : undefined;
    if (section) {
      setStage('profile');
      setStep(idx + 1);
    }
    scrollToTop();
    // Name every offending field (from its zod message) so the user
    // knows exactly which inputs to fix — not just a count, and not a
    // capped few.
    if (!section) {
      // Not a base section: the problem is in an added market's tab.
      const owner = FLAT_FORM_MARKETS.find(
        (m) => addedMarketsRef.current.includes(m.code) && invalid.some((f) => m.keys.has(f)),
      );
      if (owner) focusMarketTab(owner.code);
    }
    const targets = section ? section.fields : invalid;
    const n = invalid.length;
    toast.error(
      requiredFieldsMissingText(errors as Record<string, unknown>, targets) ??
        `Complete the ${n} required field${n > 1 ? 's' : ''} before submitting.`,
    );
  }

  // Greyed from the click until it finishes (or fails) — not just while the submit request
  // runs — so a double-click can't start a second check-save-submit behind the first.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // Every save the user's edits started — the company's (incl. a deferred flush) and each
  // market's, EU / UK included — has landed. Submit never goes ahead of the last edit.
  async function settleSaves() {
    await saveCompany(form.getValues());
    await whenSaved({
      creating: creatingRef,
      updating: updatingRef,
      pending: pendingSaveRef,
      settled: savesSettledRef,
    });
    await enqueueMarketWrite(async () => {}); // runs after every market write queued so far
  }

  async function handleSubmit() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await runSubmit();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  async function runSubmit() {
    // EU / UK keep their own forms, which the full-schema check below can't see:
    // check each added one now and show the first incomplete market instead of
    // submitting.
    // A valid tab also saves its latest values here (its own autosave may not have fired).
    for (const code of addedMarketsRef.current) {
      const validate = marketValidatorsRef.current[code];
      if (!validate) continue;
      let valid: boolean;
      try {
        valid = await validate();
      } catch {
        focusMarketTab(code);
        toast.error(`Could not save the ${marketShortName(code)} tab — please retry.`);
        return;
      }
      if (!valid) {
        focusMarketTab(code);
        toast.error(`Finish the ${marketShortName(code)} tab — the fields to fix are marked in red.`);
        return;
      }
    }

    // Amend mode: the account is already submitted and every edit has been
    // autosaved + audit-logged. "Done" just flushes the latest edit (the
    // debounce can lag the final keystrokes), then returns to the company list —
    // no re-submit (that would 409 AlreadyFinalized). Gated by form.handleSubmit,
    // same as first-time submit below: if a field is left invalid, RHF blocks
    // the callback and jumps to the first invalid section, exactly as Submit does.
    if (mode === 'amend') {
      await form.handleSubmit(async () => {
        try {
          // Section 8's signature date is the day the official certifies the account (#338):
          // re-dated only once Done goes through (a blocked Done must not re-date or save it).
          stampSignatureDate(form);
          await settleSaves();
          toast.success('Changes saved');
          void navigate({ to: customerPostSubmitRedirect('amend') });
        } catch (err) {
          toast.error(
            err instanceof Error ? err.message : 'Could not save — please retry',
          );
        }
      }, showFirstInvalidSection)();
      return;
    }

    // First-time submit (create / restored draft): validate the full schema,
    // flush, submit, then redirect to the dashboard (where the onboarding gate
    // has just unlocked the Product card).
    await form.handleSubmit(
      async () => {
        try {
          stampSignatureDate(form); // the day it's certified (#338) — only once Submit goes through
          await settleSaves();
          const cid = companyIdRef.current;
          if (cid != null) await submitMutation.mutateAsync(cid);
          // First submit = Product Account just unlocked. Hand the user
          // straight to the next onboarding step — this branch never runs
          // for amendments (those take the amend path above).
          toast.success('Company submitted successfully', {
            action: {
              label: 'Register your first product',
              onClick: () => void navigate({ to: '/product-account/new' }),
            },
          });
          void navigate({ to: customerPostSubmitRedirect(mode) });
        } catch (err) {
          toast.error(
            err instanceof Error ? err.message : 'Submit failed — please retry',
          );
        }
      },
      showFirstInvalidSection,
    )();
  }

  const readOnlyBanner = !isAdmin && (
    <div className="mb-4 rounded-lg border border-state-warning-accent bg-state-warning-bg px-4 py-3 text-state-warning-text text-[13px]">
      You&rsquo;re viewing in read-only mode. Only admins can edit the company
      account.
    </div>
  );

  return (
    <>
      <PageHero
        title="Company Account"
        subtitle={<>Comprehensive corporate profile for regulatory compliance across the jurisdictions you plan to file in. Required fields are marked with{' '} <span className="text-error font-bold">*</span>.</>}
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-6">
        {mode === 'amend' && <AmendmentBanner />}

        {!draftLoaded ? (
          <FormSkeleton fields={6} label="Loading your draft" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* One shared index across both stages: the numbered base sections
                plus a trailing "Target Markets" entry so the second stage is
                always visible (and reachable) from the profile wizard. */}
            <WizardSectionNav
              sections={SECTIONS.map((s) => ({
                num: s.num,
                title: s.name,
                invalid: sectionHasErrors(s),
                unreachable: s.num > reachCeiling,
              }))}
              currentIndex={stage === 'profile' ? step - 1 : -1}
              onPick={(i) => void handlePickSection(i)}
              busy={gateInFlight}
              trailing={{
                title: 'Target Markets',
                helper: marketsUnlocked
                  ? 'After your company profile — the markets you sell into'
                  : `Unlocks after sections 1–${TOTAL_STEPS} are complete`,
                active: stage === 'markets',
                disabled: !marketsUnlocked,
                // Same gate as the footer's "Continue to markets" CTA — this
                // entry used to skip straight past the current section's
                // validation.
                onClick: () => void handleGoToMarkets(),
              }}
            />

            <div>
              {readOnlyBanner}
              <Form {...form}>
                {/* Disabling at the fieldset level cascades to every input /
                    select / textarea without threading a prop through each
                    field. Footer buttons live outside the fieldset so
                    non-admins can still walk through to review. */}
                <form onSubmit={(e) => e.preventDefault()}>
                  {stage === 'profile' ? (
                    <>
                      <fieldset disabled={!isAdmin} className="contents">
                        <WizardSectionPanel
                          num={currentSection.num}
                          title={currentSection.name}
                          action={
                            isAdmin && (
                              <DraftStatusPill
                                state={autosave.state}
                                lastSavedAt={autosave.lastSavedAt}
                                lastError={autosave.lastError}
                              />
                            )
                          }
                        >
                          {currentSection.render(form, { parentOptions, companyId })}
                        </WizardSectionPanel>
                      </fieldset>

                      <p className="mt-5 mb-0 text-[12.5px] text-muted text-center">
                        Complete the company profile first &mdash; you&rsquo;ll
                        add each target market&rsquo;s regulatory details in
                        the next stage.
                      </p>

                      {/* Last base step advances to markets rather than
                          submitting — the real submit lives in the hub. */}
                      <WizardFooter
                        step={step}
                        totalSteps={TOTAL_STEPS}
                        isSubmitting={gateInFlight}
                        submitLabel="Continue to markets →"
                        onBack={handleBack}
                        onNext={handleNext}
                        onSubmit={handleGoToMarkets}
                      />
                    </>
                  ) : (
                    <>
                      <fieldset disabled={!isAdmin} className="contents">
                        <WizardSectionPanel
                          eyebrow="Target Markets"
                          title={form.watch('legal_company_name')?.trim() || 'Your company'}
                          action={
                            isAdmin && (
                              <DraftStatusPill
                                state={marketsSave.state}
                                lastSavedAt={marketsSave.lastSavedAt}
                                lastError={marketsSave.lastError}
                              />
                            )
                          }
                        >
                          <TargetMarketsHub
                            form={form}
                            companyId={companyId}
                            addedMarkets={addedMarkets}
                            onAddMarket={handleAddMarket}
                            onRemoveMarket={handleRemoveMarket}
                            onMarketSaveState={setMarketSave}
                            onRegisterMarketValidator={handleRegisterMarketValidator}
                            queueMarketWrite={queueMarketWrite}
                            euRequiresAuthorisedRep={euRequiresAuthorisedRep}
                            focusMarket={marketFocus}
                          />
                        </WizardSectionPanel>
                      </fieldset>

                      <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-line">
                        <p className="m-0 text-[12.5px] text-muted">
                          {mode === 'amend'
                            ? 'Every change is recorded in the audit log.'
                            : 'You can amend this account after submitting — every change is recorded in the audit log.'}
                        </p>
                        <Button
                          type="button"
                          variant="brand"
                          size="lg"
                          onClick={() => void handleSubmit()}
                          // Never greyed for a problem: clicking checks everything and
                          // takes the user to whatever needs fixing (handleSubmit).
                          disabled={submitting || submitMutation.isPending || !isAdmin}
                          title={!isAdmin ? 'Admin only' : undefined}
                        >
                          {submitting || submitMutation.isPending
                            ? 'Saving…'
                            : mode === 'amend'
                              ? 'Done'
                              : 'Submit account'}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </Form>
            </div>
          </div>
        )}
      </main>
    </>
  );
}

